// @greenlight-spec: 19-design-system.md
import * as React from 'react';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

export const DEFAULT_WIDTH = 440;
export const DEFAULT_MIN_WIDTH = 360;
export const DEFAULT_STORAGE_KEY = 'gl-inspect-panel-width';
const KEYBOARD_STEP = 24;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

export function readStoredWidth(key: string, minWidth: number, defaultWidth: number): number {
  try {
    if (typeof window === 'undefined') return defaultWidth;
    const raw = window.sessionStorage.getItem(key);
    if (raw === null) return defaultWidth;
    const parsed = parseInt(raw, 10);
    if (isNaN(parsed)) return defaultWidth;
    const max = window.innerWidth * 0.94;
    return Math.min(Math.max(parsed, minWidth), max);
  } catch {
    return defaultWidth;
  }
}

export function persistWidth(key: string, width: number): void {
  try {
    if (typeof window !== 'undefined') {
      window.sessionStorage.setItem(key, String(width));
    }
  } catch {
    // sessionStorage may be unavailable (private browsing, quota); silently ignore.
  }
}

export function clampWidth(width: number, minWidth: number): number {
  try {
    if (typeof window === 'undefined') return Math.max(width, minWidth);
    const max = window.innerWidth * 0.94;
    return Math.min(Math.max(width, minWidth), max);
  } catch {
    return Math.max(width, minWidth);
  }
}

// ---------------------------------------------------------------------------
// Options & returned handlers
// ---------------------------------------------------------------------------

export interface ResizablePanelWidthOptions {
  open: boolean;
  defaultWidth?: number;
  minWidth?: number;
  storageKey?: string;
}

export interface ResizablePanelWidthHandlers {
  width: number;
  isDragging: boolean;
  contentRef: React.RefObject<HTMLDivElement>;
  handlePointerDown: (e: React.PointerEvent<HTMLDivElement>) => void;
  handlePointerMove: (e: React.PointerEvent<HTMLDivElement>) => void;
  handlePointerUp: (e: React.PointerEvent<HTMLDivElement>) => void;
  handleKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) => void;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Manages width state and pointer/keyboard resize logic for the panel-desktop
 * surface of GLDrawer. Persists the chosen width to sessionStorage under
 * `storageKey` and clamps between `minWidth` and 94% of the viewport.
 */
export function useResizablePanelWidth({
  open,
  defaultWidth = DEFAULT_WIDTH,
  minWidth = DEFAULT_MIN_WIDTH,
  storageKey = DEFAULT_STORAGE_KEY,
}: ResizablePanelWidthOptions): ResizablePanelWidthHandlers {
  const [width, setWidth] = React.useState<number>(() =>
    readStoredWidth(storageKey, minWidth, defaultWidth),
  );

  const prevOpenRef = React.useRef(open);
  React.useEffect(() => {
    if (open && !prevOpenRef.current) {
      setWidth(readStoredWidth(storageKey, minWidth, defaultWidth));
    }
    prevOpenRef.current = open;
  }, [open, storageKey, minWidth, defaultWidth]);

  const contentRef = React.useRef<HTMLDivElement>(null);
  const draggingRef = React.useRef(false);
  const startXRef = React.useRef(0);
  const startWidthRef = React.useRef(0);
  const [isDragging, setIsDragging] = React.useState(false);

  const handlePointerDown = React.useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      e.preventDefault();
      draggingRef.current = true;
      startXRef.current = e.clientX;
      startWidthRef.current = width;
      setIsDragging(true);
      if (typeof e.currentTarget.setPointerCapture === 'function') {
        e.currentTarget.setPointerCapture(e.pointerId);
      }
    },
    [width],
  );

  const handlePointerMove = React.useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!draggingRef.current) return;
      const next = startWidthRef.current + (startXRef.current - e.clientX);
      setWidth(clampWidth(next, minWidth));
    },
    [minWidth],
  );

  const handlePointerUp = React.useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!draggingRef.current) return;
      draggingRef.current = false;
      setIsDragging(false);
      if (typeof e.currentTarget.releasePointerCapture === 'function') {
        e.currentTarget.releasePointerCapture(e.pointerId);
      }
      const final = clampWidth(startWidthRef.current + (startXRef.current - e.clientX), minWidth);
      setWidth(final);
      persistWidth(storageKey, final);
    },
    [minWidth, storageKey],
  );

  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        setWidth((prev) => {
          const next = clampWidth(prev + KEYBOARD_STEP, minWidth);
          persistWidth(storageKey, next);
          return next;
        });
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        setWidth((prev) => {
          const next = clampWidth(prev - KEYBOARD_STEP, minWidth);
          persistWidth(storageKey, next);
          return next;
        });
      }
    },
    [minWidth, storageKey],
  );

  return {
    width,
    isDragging,
    contentRef,
    handlePointerDown,
    handlePointerMove,
    handlePointerUp,
    handleKeyDown,
  };
}
